import { type NumberFormatters, useNumberFormatters } from "@sg-protect/components";
import { useLocation, useNavigation } from "react-router";
import { usePollOverviewQuery } from "#api/routes/tenant-assessment.ts";
import type { AssessmentOverview, Int32DataPoint } from "#api/types.ts";
import { ASSESSMENT_TABS } from "#components/assessmentTabs.ts";
import { usePaths } from "#paths.ts";
import type { FiguresState } from "#utils/dataSources.ts";

type AssessmentTab = (typeof ASSESSMENT_TABS)[number];

export interface AssessmentSection extends Pick<AssessmentTab, "pathKey" | "label" | "icon"> {
    href: string;
    isActive: boolean;
    total: FiguresState;
}

interface ResolveTotalStateInput {
    getTotal: AssessmentTab["getTotal"];
    overview: AssessmentOverview | undefined;
    isError: boolean;
    formatNumber: NumberFormatters["formatNumber"];
}

function toTotalState(
    dataPoint: Int32DataPoint | undefined,
    formatNumber: NumberFormatters["formatNumber"]
): FiguresState {
    if (dataPoint === undefined || dataPoint.status === "failed") {
        return { kind: "failed" };
    }

    if (dataPoint.status === "notInitialized") {
        return { kind: "computing" };
    }

    return { kind: "ready", text: formatNumber(dataPoint.value) };
}

function resolveTotalState({ getTotal, overview, isError, formatNumber }: ResolveTotalStateInput): FiguresState {
    if (getTotal === undefined) {
        return { kind: "none" };
    }

    if (overview !== undefined) {
        return toTotalState(getTotal(overview), formatNumber);
    }

    return isError ? { kind: "failed" } : { kind: "loading" };
}

export function useAssessmentSections(): AssessmentSection[] {
    const paths = usePaths();
    const { pathname } = useLocation();
    const navigation = useNavigation();
    const activePathname = navigation.location?.pathname ?? pathname;
    const { data: overview, isError } = usePollOverviewQuery({ isPolling: false });
    const { formatNumber } = useNumberFormatters();

    return ASSESSMENT_TABS.map(({ pathKey, label, icon, getTotal }) => {
        const href = paths[pathKey];

        return {
            pathKey,
            label,
            icon,
            href,
            isActive: href === activePathname,
            total: resolveTotalState({ getTotal, overview, isError, formatNumber })
        };
    });
}
